import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { LangTabs } from '../../components/ui/lang-tabs';
import { toast } from 'sonner';
import { HelpCircle, Save, Plus, Trash2, GripVertical } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminFAQPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ items: [], items_pt: [], items_en: [] });
  const [lang, setLang] = useState('ja');

  const itemsKey = lang === 'ja' ? 'items' : `items_${lang}`;
  const items = data[itemsKey] || [];

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings/faq`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      const { key, updated_at, updated_by, ...rest } = res.data;
      setData(prev => ({ ...prev, ...rest }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setItems = (newItems) => setData(prev => ({ ...prev, [itemsKey]: newItems }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/faq`, data, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
      toast.success(t('faq_saved'));
    } catch { toast.error(t('content_save_failed')); }
    finally { setSaving(false); }
  };

  const addItem = () => setItems([...items, { question: '', answer: '' }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };
  const moveItem = (idx, direction) => {
    const updated = [...items];
    const target = idx + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    setItems(updated);
  };

  if (loading) return <AdminLayout><div className="text-center py-12 text-muted-foreground">{t('loading')}</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-faq-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><HelpCircle className="h-8 w-8" />{t('faq_admin_title')}</h1>
            <p className="text-muted-foreground mt-1">{t('faq_admin_subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <LangTabs activeLang={lang} onChange={setLang} />
            <Button variant="outline" onClick={addItem} className="gap-2" data-testid="add-faq-btn"><Plus className="h-4 w-4" />{t('faq_add')}</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-faq-btn"><Save className="h-4 w-4" />{saving ? t('admin_settings_saving') : t('faq_save_btn')}</Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
            <HelpCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-muted-foreground mb-4">{lang !== 'ja' ? `${lang.toUpperCase()}のFAQが未作成です` : t('faq_empty')}</p>
            <Button variant="outline" onClick={addItem} className="gap-2"><Plus className="h-4 w-4" />{t('faq_add')}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, idx) => (
              <div key={idx} className="bg-card border border-border/50 rounded-xl p-5" data-testid={`faq-item-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-2">
                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-secondary disabled:opacity-20"><GripVertical className="h-4 w-4 text-muted-foreground" /></button>
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-accent bg-accent/10 px-2 py-0.5 rounded">Q{idx + 1}</span>
                      <Input value={item.question} onChange={(e) => updateItem(idx, 'question', e.target.value)} placeholder={t('faq_question_placeholder')} className="flex-1" data-testid={`faq-q-${idx}`} />
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-primary bg-secondary px-2 py-0.5 rounded mt-2">A</span>
                      <textarea value={item.answer} onChange={(e) => updateItem(idx, 'answer', e.target.value)} rows={3} className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30" data-testid={`faq-a-${idx}`} />
                    </div>
                  </div>
                  <button onClick={() => removeItem(idx)} className="p-2 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 mt-1" data-testid={`faq-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
